import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Page, Screen, ScreenHeader, Section, StickyCta } from "../components/AppShell";
import { Button, Skeleton, Spinner, StateMessage } from "../components/ui";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import {
  Checkbox,
  Field,
  NumberInput,
  Segmented,
  TextArea,
  TextInput,
  TripleNumber,
} from "../components/fields";
import { PartsEditor } from "../features/artworks/PartsEditor";
import { MultiPicker, Picker, type PickerOption } from "../components/Picker";
import { ArtistPicker } from "../features/artists/ArtistPicker";
import {
  FileSlot,
  MediaSlot,
  useRevokeOnUnmount,
  type StagedFile,
} from "../features/media/MediaPicker";
import {
  blankForm,
  formFromArtwork,
  toPayload,
  useArtworkForm,
  type FormState,
} from "../features/artworks/useArtworkForm";
import {
  useAddSubtype,
  useAddTerm,
  useAddType,
  useArtwork,
  useCreateArtwork,
  useDeleteAttachment,
  useMakePrimary,
  useUpdateAttachment,
  useMe,
  useTaxonomy,
  useUpdateArtwork,
  useUploadedMedia,
  useVocabulary,
} from "../api/queries";
import { uploadAttachment } from "../api/upload";
import { MEDIA_RIGHTS_ENABLED } from "../features/artworks/flags";
import { setClosingConfirmation } from "../telegram/webapp";
import { haptic } from "../telegram/haptics";
import { listItem, sectionStagger } from "../motion";
import { fieldLabels } from "../i18n/fieldLabels";
import { pick } from "../i18n/labels";
import { strings, useT } from "../i18n/strings";
import { messagesOf } from "../api/errors";
import type { AccessLevel, AttachmentKind, RightsLicense, Term } from "../api/types";
import s from "../features/artworks/form.module.css";

const termOptions = (terms: Term[] | undefined): PickerOption[] =>
  (terms ?? []).map((term) => ({
    id: term.id,
    fa: term.label_fa,
    en: term.label_en,
    isSeeded: term.is_seeded,
  }));

/** Order is fixed; the labels are resolved per render. */
const YEAR_VALUES = ["exact", "circa", "range", "unknown"] as const;

const RIGHTS_VALUES: RightsLicense[] = [
  "all_rights_reserved",
  "cc_by",
  "cc_by_sa",
  "cc_by_nd",
  "cc_by_nc",
  "cc_by_nc_sa",
  "cc_by_nc_nd",
  "cc0",
  "public_domain",
  "custom",
];

const ACCESS_VALUES = ["private", "unlisted", "public"] as const;

/**
 * Both languages at once, the way `termOptions` reads `label_fa`/`label_en` off a
 * server row — `Picker` shows the second line only where the two differ, which is
 * exactly right for the Creative Commons names.
 */
const rightsOptions = (): PickerOption[] =>
  RIGHTS_VALUES.map((value) => ({
    id: value,
    fa: strings.fa.media.rightsLicense[value],
    en: strings.en.media.rightsLicense[value],
  }));

export default function ArtworkForm() {
  const t = useT();
  const yearOptions = YEAR_VALUES.map((value) => ({
    value,
    label: t.form.yearPrecision[value],
  }));
  const { id } = useParams();
  const editing = Boolean(id);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const me = useMe();
  const existing = useArtwork(id);
  const taxonomy = useTaxonomy();

  const mediums = useVocabulary("medium");
  const styles = useVocabulary("style");
  const ownership = useVocabulary("ownership_status");
  const condition = useVocabulary("condition_status");
  const confidence = useVocabulary("provenance_confidence");
  const currency = useVocabulary("currency");

  const initial = useMemo<FormState>(() => ({ ...blankForm, type: params.get("type") }), [params]);
  const form = useArtworkForm(initial);
  const { state, set, reset, errors, dirty, check, applyServerErrors } = form;

  const [staged, setStaged] = useState<StagedFile[]>([]);
  /**
   * Which media file is the thumbnail: a staged file's key, or a saved
   * attachment's id. It is a choice, not a kind — `save` settles it with one
   * `make-primary` call once everything that had to upload has uploaded.
   */
  const [primaryKey, setPrimaryKey] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useRevokeOnUnmount(staged);

  const create = useCreateArtwork();
  const update = useUpdateArtwork(id ?? "");
  const uploaded = useUploadedMedia();
  const removeAttachment = useDeleteAttachment(id ?? "");
  const patchAttachment = useUpdateAttachment(id ?? "");
  const makePrimary = useMakePrimary(id ?? "");

  const addMedium = useAddTerm("medium");
  const addStyle = useAddTerm("style");
  const addOwnership = useAddTerm("ownership_status");
  const addCondition = useAddTerm("condition_status");
  const addConfidence = useAddTerm("provenance_confidence");
  const addCurrency = useAddTerm("currency");
  const addType = useAddType();
  const addSubtype = useAddSubtype(state.type);

  // Load the record into the form exactly once, not on every background refetch.
  const loadedId = existing.data?.id;
  const savedPrimary = existing.data?.attachments.find((file) => file.kind === "primary") ?? null;
  const savedPrimaryId = savedPrimary?.id ?? null;
  useEffect(() => {
    if (existing.data && loadedId === id) {
      reset(formFromArtwork(existing.data));
      setPrimaryKey(existing.data.attachments.find((file) => file.kind === "primary")?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  // Telegram closes on a swipe-down; unsaved work deserves a prompt first.
  const thumbnailMoved = primaryKey !== savedPrimaryId;
  useEffect(() => {
    setClosingConfirmation((dirty || thumbnailMoved) && !saved);
    return () => setClosingConfirmation(false);
  }, [dirty, thumbnailMoved, saved]);

  const types: PickerOption[] = (taxonomy.data ?? []).map((type) => ({
    id: type.id,
    fa: type.label_fa,
    en: type.label_en,
    isSeeded: type.is_seeded,
  }));

  const parent = taxonomy.data?.find((type) => type.id === state.type);
  const subtypes: PickerOption[] = (parent?.subtypes ?? []).map((subtype) => ({
    id: subtype.id,
    fa: subtype.label_fa,
    en: subtype.label_en,
    isSeeded: subtype.is_seeded,
  }));

  const stageFiles = useCallback(
    (files: StagedFile[]) => setStaged((current) => [...current, ...files]),
    [],
  );

  const unstage = useCallback((key: string) => {
    // The thumbnail cannot be a file that is no longer there.
    setPrimaryKey((current) => (current === key ? null : current));
    setStaged((current) => {
      const target = current.find((item) => item.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.key !== key);
    });
  }, []);

  const deleteExisting = useCallback(
    (attachmentId: string) => {
      setPrimaryKey((current) => (current === attachmentId ? null : current));
      removeAttachment.mutate(attachmentId, {
        onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
      });
    },
    [removeAttachment, toast],
  );

  const attachmentsOf = (kind: AttachmentKind) =>
    existing.data?.attachments.filter((file) => file.kind === kind) ?? [];

  /** One section, thumbnail first — the hero and the rest of the same shoot. */
  const mediaAttachments = [...attachmentsOf("primary"), ...attachmentsOf("gallery")];

  const stagedOf = (kind: AttachmentKind) => staged.filter((item) => item.kind === kind);

  /**
   * Media can only be signed against an artwork that exists, so a new record is
   * written first and its staged files are uploaded against the id it returns.
   */
  const runUploads = async (artworkId: string) => {
    const landed = new Map<string, string>();
    for (const item of staged) {
      setProgress((current) => ({ ...current, [item.key]: 0 }));
      try {
        const attachment = await uploadAttachment({
          artworkId,
          kind: item.kind,
          file: item.file,
          onProgress: (fraction) =>
            setProgress((current) => ({ ...current, [item.key]: fraction })),
        });
        // Paint the record with what the confirm call just handed back, so the
        // screen we are about to navigate to has the hero on its first frame.
        uploaded.absorb(artworkId, attachment);
        landed.set(item.key, attachment.id);
      } catch (error) {
        toast.show(`${item.file.name}: ${messagesOf(error)[0]}`, { tone: "error" });
      } finally {
        setProgress((current) => {
          const next = { ...current };
          delete next[item.key];
          return next;
        });
      }
    }
    // Clearing the list bypasses `useRevokeOnUnmount`, which only ever sees what
    // is still staged — so the previews of everything that uploaded successfully
    // would leak for the life of the page.
    setStaged((current) => {
      for (const item of current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    // Do not settle here: makePrimary has not run yet, and a refetch would
    // paint the new file as gallery under the previous primary. save() settles
    // once after promotion.
    return landed;
  };

  const save = async () => {
    if (saving) return;
    if (!check()) {
      haptic.error();
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(state);
      const artwork = editing
        ? await update.mutateAsync(payload)
        : await create.mutateAsync(payload);

      const landed = staged.length ? await runUploads(artwork.id) : new Map<string, string>();

      // Everything uploaded as gallery media, so the thumbnail is settled here:
      // one promotion, which demotes whichever file held the mark before it. A
      // staged file whose upload failed has no id and cannot take the mark.
      const stagedKey = staged.some((item) => item.key === primaryKey);
      const heroId = primaryKey
        ? (landed.get(primaryKey) ?? (stagedKey ? null : primaryKey))
        : null;
      if (heroId && heroId !== savedPrimaryId) {
        await makePrimary.mutateAsync(heroId);
      }
      // Always wait for a fresh record before opening detail: absorb/makePrimary
      // can race a create/update refetch that still has the previous hero kinds.
      if (landed.size > 0 || (heroId && heroId !== savedPrimaryId)) {
        await uploaded.settle(artwork.id);
      }

      // Rights live on whichever file is the hero, so they follow the mark.
      const hero = heroId ?? savedPrimaryId;
      if (
        MEDIA_RIGHTS_ENABLED &&
        hero &&
        (hero !== savedPrimary?.id ||
          savedPrimary.rights !== state.media_rights ||
          savedPrimary.rights_note !== state.media_rights_note ||
          savedPrimary.access_level !== state.media_access_level)
      ) {
        await patchAttachment.mutateAsync({
          id: hero,
          rights: state.media_rights,
          rights_note: state.media_rights_note,
          access_level: state.media_access_level,
        });
      }

      setSaved(true);
      haptic.success();
      setClosingConfirmation(false);
      navigate(`/artwork/${artwork.id}`, { replace: true });
    } catch (error) {
      applyServerErrors(error);
      haptic.error();
      toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (editing && existing.isPending) return <FormSkeleton />;

  if (editing && existing.error) {
    return (
      <Screen withTabs={false}>
        <ScreenHeader title={t.form.editTitle} />
        <StateMessage
          icon="alert"
          title={t.errors.title}
          body={messagesOf(existing.error)[0]}
          action={
            <Button variant="secondary" onClick={() => navigate(-1)}>
              {t.actions.back}
            </Button>
          }
        />
      </Screen>
    );
  }

  const limits = me.data?.limits;

  return (
    <Screen withTabs={false}>
      <ScreenHeader sticky bordered title={editing ? t.form.editTitle : t.form.newTitle} />

      <Page>
        <motion.div variants={sectionStagger} initial="hidden" animate="show" className={s.form}>
          {/* ---------------------------------------------------- identity */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.identity} icon="works">
              <Field label={fieldLabels.title} error={errors.title} htmlFor="title">
                <TextInput
                  id="title"
                  auto
                  value={state.title}
                  invalid={Boolean(errors.title)}
                  placeholder={t.form.titlePlaceholder}
                  onChange={(value) => set("title", value)}
                />
              </Field>

              <Field label={fieldLabels.artist} optional>
                <ArtistPicker
                  value={state.artist}
                  valueName={state.artistName}
                  onChange={(artistId, name) => form.patch({ artist: artistId, artistName: name })}
                />
              </Field>

              <Field
                label={fieldLabels.inventory_code}
                optional
                error={errors.inventory_code}
                htmlFor="inventory"
              >
                <TextInput
                  id="inventory"
                  value={state.inventory_code}
                  placeholder={t.form.inventoryPlaceholder}
                  onChange={(value) => set("inventory_code", value)}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------------------- media */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.media} icon="image">
              <Field label={fieldLabels.primary_media} optional>
                <MediaSlot
                  existing={mediaAttachments}
                  staged={stagedOf("gallery")}
                  primaryKey={primaryKey}
                  onPickPrimary={setPrimaryKey}
                  limits={limits}
                  artworkId={id ?? null}
                  progress={progress}
                  onStage={stageFiles}
                  onUnstage={unstage}
                  onDeleteExisting={deleteExisting}
                />
              </Field>

              {MEDIA_RIGHTS_ENABLED && (
                <>
                  <Field label={fieldLabels.rights} optional>
                    <Picker
                      title={pick(fieldLabels.rights)}
                      options={rightsOptions()}
                      value={state.media_rights || null}
                      placeholder={t.form.none}
                      onChange={(value) => set("media_rights", (value ?? "") as RightsLicense | "")}
                    />
                  </Field>

                  {state.media_rights === "custom" && (
                    <Field
                      label={fieldLabels.rights_note}
                      error={errors.media_rights_note}
                      htmlFor="rights-note"
                    >
                      <TextInput
                        id="rights-note"
                        auto
                        value={state.media_rights_note}
                        invalid={Boolean(errors.media_rights_note)}
                        placeholder={t.media.rightsNotePlaceholder}
                        onChange={(value) => set("media_rights_note", value)}
                      />
                    </Field>
                  )}

                  <Field label={fieldLabels.access_level}>
                    <Segmented
                      name="access"
                      value={state.media_access_level}
                      options={ACCESS_VALUES.map((value) => ({
                        value,
                        label: t.media.accessLevel[value],
                      }))}
                      onChange={(value) => set("media_access_level", value as AccessLevel)}
                    />
                  </Field>

                  {/* Owner-only in v1. This is recorded for later, and the copy says so
                    rather than implying a share feature that does not exist. */}
                  <p className={s.note}>
                    <Icon name="info" size={16} />
                    <span>{t.media.accessNote}</span>
                  </p>
                </>
              )}
            </Section>
          </motion.div>

          {/* ---------------------------------------------- classification */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.classification} icon="type">
              <Field label={fieldLabels.type} error={errors.type}>
                <Picker
                  title={pick(fieldLabels.type)}
                  options={types}
                  value={state.type}
                  clearable={false}
                  // A subtype belongs to one type; changing the type strands it.
                  onChange={(typeId) => form.patch({ type: typeId, subtype: null })}
                  onAddOption={async (labelText) => (await addType.mutateAsync(labelText)).id}
                />
              </Field>

              <Field label={fieldLabels.subtype} optional error={errors.subtype}>
                <Picker
                  title={pick(fieldLabels.subtype)}
                  options={subtypes}
                  value={state.subtype}
                  disabled={!state.type}
                  placeholder={state.type ? t.form.none : t.browse.pickTypeFirst}
                  onChange={(value) => set("subtype", value)}
                  onAddOption={
                    state.type
                      ? async (labelText) => (await addSubtype.mutateAsync(labelText)).id
                      : undefined
                  }
                />
              </Field>
            </Section>
          </motion.div>

          {/* -------------------------------------------------------- year */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.year} icon="year">
              <Segmented
                name="year-precision"
                value={state.year_precision}
                options={yearOptions}
                onChange={(value) =>
                  form.patch(
                    value === "unknown"
                      ? { year_precision: value, year_start: null, year_end: null }
                      : { year_precision: value },
                  )
                }
              />

              {state.year_precision !== "unknown" && (
                <div className={s.yearRow}>
                  <Field
                    label={
                      state.year_precision === "range"
                        ? { fa: t.filters.yearFrom, en: "From" }
                        : fieldLabels.year
                    }
                    error={errors.year_start}
                  >
                    <NumberInput
                      step="1"
                      value={state.year_start}
                      invalid={Boolean(errors.year_start)}
                      onChange={(value) => set("year_start", value)}
                    />
                  </Field>

                  {state.year_precision === "range" && (
                    <Field label={{ fa: t.filters.yearTo, en: "To" }} error={errors.year_end}>
                      <NumberInput
                        step="1"
                        value={state.year_end}
                        invalid={Boolean(errors.year_end)}
                        onChange={(value) => set("year_end", value)}
                      />
                    </Field>
                  )}
                </div>
              )}
            </Section>
          </motion.div>

          {/* -------------------------------------------------- dimensions */}
          <motion.div variants={listItem}>
            <Section title={`${t.form.sections.dimensions} · ${t.form.unit}`} icon="dimensions">
              <Field
                label={fieldLabels.artworkDimensions}
                optional
                error={
                  errors.artwork_width_cm ||
                  errors.artwork_height_cm ||
                  errors.artwork_depth_cm
                }
              >
                <TripleNumber
                  values={[state.artwork_width_cm, state.artwork_height_cm, state.artwork_depth_cm]}
                  labels={[
                    pick(fieldLabels.width),
                    pick(fieldLabels.height),
                    pick(fieldLabels.depth),
                  ]}
                  onChange={(index, value) =>
                    set(
                      (["artwork_width_cm", "artwork_height_cm", "artwork_depth_cm"] as const)[
                        index
                      ],
                      value,
                    )
                  }
                />
              </Field>

              <Field
                label={fieldLabels.frameDimensions}
                optional
                error={errors.frame_width_cm || errors.frame_height_cm || errors.frame_depth_cm}
              >
                <TripleNumber
                  values={[state.frame_width_cm, state.frame_height_cm, state.frame_depth_cm]}
                  labels={[
                    pick(fieldLabels.width),
                    pick(fieldLabels.height),
                    pick(fieldLabels.depth),
                  ]}
                  onChange={(index, value) =>
                    set(
                      (["frame_width_cm", "frame_height_cm", "frame_depth_cm"] as const)[index],
                      value,
                    )
                  }
                />
              </Field>

              <Field label={fieldLabels.is_life_size} optional>
                <Checkbox
                  checked={state.is_life_size}
                  onChange={(value) => set("is_life_size", value)}
                >
                  {pick(fieldLabels.is_life_size)}
                </Checkbox>
              </Field>

              <div className={s.yearRow}>
                <Field
                  label={fieldLabels.edition_number}
                  optional
                  error={errors.edition_number}
                >
                  <NumberInput
                    step="1"
                    min={1}
                    value={state.edition_number}
                    invalid={Boolean(errors.edition_number)}
                    onChange={(value) => set("edition_number", value)}
                  />
                </Field>
                <Field label={fieldLabels.edition_size} optional error={errors.edition_size}>
                  <NumberInput
                    step="1"
                    min={1}
                    value={state.edition_size}
                    invalid={Boolean(errors.edition_size)}
                    onChange={(value) => set("edition_size", value)}
                  />
                </Field>
              </div>

              <Field label={fieldLabels.parts} optional>
                <PartsEditor
                  parts={state.parts}
                  errors={errors}
                  onAdd={() =>
                    form.patch({
                      parts: [
                        ...state.parts,
                        {
                          key: crypto.randomUUID(),
                          label: "",
                          width_cm: null,
                          height_cm: null,
                          depth_cm: null,
                        },
                      ],
                    })
                  }
                  onRemove={(index) =>
                    form.patch({ parts: state.parts.filter((_, i) => i !== index) })
                  }
                  onChange={(index, changes) =>
                    form.patch({
                      parts: state.parts.map((part, i) =>
                        i === index ? { ...part, ...changes } : part,
                      ),
                    })
                  }
                />
              </Field>
            </Section>
          </motion.div>

          {/* ---------------------------------------------- medium + style */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.material} icon="medium">
              {/* Medium is materials and technique. It is not the subtype. */}
              <Field label={fieldLabels.mediums} optional>
                <MultiPicker
                  title={pick(fieldLabels.mediums)}
                  options={termOptions(mediums.data)}
                  values={state.mediums}
                  onChange={(ids) => set("mediums", ids)}
                  onAddOption={async (labelText) => (await addMedium.mutateAsync(labelText)).id}
                />
              </Field>

              <Field label={fieldLabels.style} optional>
                <Picker
                  title={pick(fieldLabels.style)}
                  options={termOptions(styles.data)}
                  value={state.style}
                  onChange={(value) => set("style", value)}
                  onAddOption={async (labelText) => (await addStyle.mutateAsync(labelText)).id}
                />
              </Field>

              <Field label={fieldLabels.curatorial_notes} optional>
                <TextArea
                  value={state.curatorial_notes}
                  onChange={(value) => set("curatorial_notes", value)}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------ ownership + value */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.ownership} icon="ownership">
              <Field label={fieldLabels.ownership_status} optional>
                <Picker
                  title={pick(fieldLabels.ownership_status)}
                  options={termOptions(ownership.data)}
                  value={state.ownership_status}
                  onChange={(value) => set("ownership_status", value)}
                  onAddOption={async (labelText) => (await addOwnership.mutateAsync(labelText)).id}
                />
              </Field>

              {/* Three independent amounts. Any may be empty; a currency is
                  required the moment one of them is not. */}
              <div className={s.moneyGrid}>
                <Field label={fieldLabels.price_asking} optional error={errors.price_asking}>
                  <NumberInput
                    min={0}
                    value={state.price_asking}
                    onChange={(value) => set("price_asking", value)}
                  />
                </Field>
                <Field label={fieldLabels.price_sold} optional error={errors.price_sold}>
                  <NumberInput
                    min={0}
                    value={state.price_sold}
                    onChange={(value) => set("price_sold", value)}
                  />
                </Field>
                <Field label={fieldLabels.price_estimate} optional error={errors.price_estimate}>
                  <NumberInput
                    min={0}
                    value={state.price_estimate}
                    onChange={(value) => set("price_estimate", value)}
                  />
                </Field>
              </div>

              <Field label={fieldLabels.currency} optional error={errors.currency}>
                <Picker
                  title={pick(fieldLabels.currency)}
                  options={(currency.data ?? []).map((term) => ({
                    id: term.id,
                    fa: term.label_fa,
                    en: term.code || term.label_en,
                    isSeeded: term.is_seeded,
                  }))}
                  value={state.currency}
                  onChange={(value) => set("currency", value)}
                  onAddOption={async (labelText) => (await addCurrency.mutateAsync(labelText)).id}
                />
              </Field>
            </Section>
          </motion.div>

          {/* --------------------------------------------------- condition */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.condition} icon="condition">
              <Field label={fieldLabels.condition_status} optional>
                <Picker
                  title={pick(fieldLabels.condition_status)}
                  options={termOptions(condition.data)}
                  value={state.condition_status}
                  onChange={(value) => set("condition_status", value)}
                  onAddOption={async (labelText) => (await addCondition.mutateAsync(labelText)).id}
                />
              </Field>

              <Field label={fieldLabels.condition_notes} optional>
                <TextArea
                  value={state.condition_notes}
                  onChange={(value) => set("condition_notes", value)}
                />
              </Field>

              <Field label={fieldLabels.condition_files} optional>
                <FileSlot
                  kind="condition_report"
                  addLabel={t.media.pickFile}
                  existing={attachmentsOf("condition_report")}
                  staged={stagedOf("condition_report")}
                  limits={limits}
                  artworkId={id ?? null}
                  progress={progress}
                  onStage={stageFiles}
                  onUnstage={unstage}
                  onDeleteExisting={deleteExisting}
                />
              </Field>
            </Section>
          </motion.div>

          {/* -------------------------------------------------- provenance */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.provenance} icon="provenance">
              <Field label={fieldLabels.provenance_text} optional>
                <TextArea
                  value={state.provenance_text}
                  onChange={(value) => set("provenance_text", value)}
                />
              </Field>

              <Field label={fieldLabels.provenance_confidence} optional>
                <Picker
                  title={pick(fieldLabels.provenance_confidence)}
                  options={termOptions(confidence.data)}
                  value={state.provenance_confidence}
                  onChange={(value) => set("provenance_confidence", value)}
                  onAddOption={async (labelText) => (await addConfidence.mutateAsync(labelText)).id}
                />
              </Field>
            </Section>
          </motion.div>

          {/* ------------------------------------------------ authenticity */}
          <motion.div variants={listItem}>
            <Section title={t.form.sections.authenticity} icon="authenticity">
              {/* Evidence, never a verdict — see DESIGN.md and the blueprint. */}
              <p className={s.note}>
                <Icon name="info" size={16} />
                <span>{t.detail.authenticityNote}</span>
              </p>

              <Field label={fieldLabels.authenticity_notes} optional>
                <TextArea
                  value={state.authenticity_notes}
                  onChange={(value) => set("authenticity_notes", value)}
                />
              </Field>

              <Field label={fieldLabels.authenticity_files} optional>
                <FileSlot
                  kind="authenticity_evidence"
                  addLabel={t.media.pickFile}
                  existing={attachmentsOf("authenticity_evidence")}
                  staged={stagedOf("authenticity_evidence")}
                  limits={limits}
                  artworkId={id ?? null}
                  progress={progress}
                  onStage={stageFiles}
                  onUnstage={unstage}
                  onDeleteExisting={deleteExisting}
                />
              </Field>
            </Section>
          </motion.div>

          <div className={s.ctaSpacer} />
        </motion.div>
      </Page>

      <StickyCta>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? (
            <>
              <Spinner size={20} />
              {staged.length ? t.media.uploading : t.actions.saving}
            </>
          ) : (
            <>
              <Icon name="check" size={20} />
              {t.actions.save}
            </>
          )}
        </Button>
      </StickyCta>
    </Screen>
  );
}

function FormSkeleton() {
  const t = useT();
  return (
    <Screen withTabs={false}>
      <ScreenHeader sticky bordered title={t.form.editTitle} />
      <Page>
        <div className={s.form}>
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className={s.skeletonBlock}>
              <Skeleton height={14} width="30%" radius="4px" />
              <Skeleton height={44} radius="var(--radius-card)" />
            </div>
          ))}
        </div>
      </Page>
    </Screen>
  );
}
